import React, { useState } from 'react';
import { useAuth } from './AuthProvider';
import { Flame, X, Mail, ArrowRight, Loader2 } from 'lucide-react';

interface AuthModalProps {
  onClose: () => void;
  isSignUp: boolean;
  setIsSignUp: (value: boolean) => void;
}

export default function AuthModal({ onClose, isSignUp, setIsSignUp }: AuthModalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const { signIn, signUp, resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (showForgotPassword) {
        await resetPassword(email);
        setResetSent(true);
      } else if (isSignUp) {
        await signUp(email, password);
        setVerificationSent(true);
      } else {
        await signIn(email, password);
        onClose();
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (verificationSent) {
    return (
      <div className="fixed inset-0 bg-midnight-900/90 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-midnight-800 rounded-xl p-8 shadow-xl max-w-md w-full relative border border-midnight-700">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-midnight-400 hover:text-midnight-300"
          >
            <X className="h-5 w-5" />
          </button>
          
          <div className="flex items-center justify-center mb-6">
            <Mail className="h-12 w-12 text-amber-500" />
          </div>
          
          <h2 className="text-2xl font-bold text-center mb-4 text-midnight-50">
            Verify Your Email
          </h2>
          
          <p className="text-center text-midnight-300 mb-6">
            We've sent a verification link to <strong>{email}</strong>. Please check your email and click the link to complete your registration.
          </p>
          
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-amber-500 to-marigold-500 text-white py-2 px-4 rounded-lg hover:from-amber-400 hover:to-marigold-400 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (resetSent) {
    return (
      <div className="fixed inset-0 bg-midnight-900/90 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-midnight-800 rounded-xl p-8 shadow-xl max-w-md w-full relative border border-midnight-700">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-midnight-400 hover:text-midnight-300"
          >
            <X className="h-5 w-5" />
          </button>
          
          <div className="flex items-center justify-center mb-6">
            <Mail className="h-12 w-12 text-amber-500" />
          </div>
          
          <h2 className="text-2xl font-bold text-center mb-4 text-midnight-50">
            Check Your Email
          </h2>
          
          <p className="text-center text-midnight-300 mb-6">
            We've sent password reset instructions to <strong>{email}</strong>. Please check your email to continue.
          </p>
          
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-amber-500 to-marigold-500 text-white py-2 px-4 rounded-lg hover:from-amber-400 hover:to-marigold-400 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-midnight-900/90 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-midnight-800 rounded-xl p-8 shadow-xl max-w-md w-full relative border border-midnight-700">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-midnight-400 hover:text-midnight-300"
        >
          <X className="h-5 w-5" />
        </button>
        
        <div className="flex items-center justify-center mb-6">
          <Flame className="h-8 w-8 text-amber-500" />
        </div>

        <h2 className="text-2xl font-bold text-center mb-6 text-midnight-50">
          {showForgotPassword 
            ? 'Reset Password'
            : isSignUp 
              ? 'Create your account' 
              : 'Welcome back'}
        </h2>
        
        {error && (
          <div className="mb-4 p-3 bg-red-900/20 text-red-400 rounded-lg text-sm border border-red-900/50">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-midnight-200 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 bg-midnight-900/50 border border-midnight-700 rounded-lg focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 text-midnight-100"
              required
            />
          </div>
          
          {!showForgotPassword && (
            <div>
              <label className="block text-sm font-medium text-midnight-200 mb-1">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 bg-midnight-900/50 border border-midnight-700 rounded-lg focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 text-midnight-100"
                required
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-marigold-500 text-white py-2 px-4 rounded-lg hover:from-amber-400 hover:to-marigold-400 transition-colors font-medium disabled:opacity-50 flex items-center justify-center space-x-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Please wait...</span>
              </>
            ) : (
              <>
                <ArrowRight className="h-5 w-5" />
                <span>
                  {showForgotPassword 
                    ? 'Send Reset Instructions'
                    : isSignUp 
                      ? 'Sign Up' 
                      : 'Sign In'}
                </span>
              </>
            )}
          </button>
        </form>

        <div className="mt-4 text-center space-y-2">
          {!showForgotPassword && (
            <button
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-amber-400 hover:text-amber-300 text-sm"
            >
              {isSignUp
                ? 'Already have an account? Sign in'
                : "Don't have an account? Sign up"}
            </button>
          )}

          {!isSignUp && !showForgotPassword && (
            <div>
              <button
                onClick={() => setShowForgotPassword(true)}
                className="text-midnight-400 hover:text-midnight-300 text-sm"
              >
                Forgot your password?
              </button>
            </div>
          )}

          {showForgotPassword && (
            <button
              onClick={() => setShowForgotPassword(false)}
              className="text-amber-400 hover:text-amber-300 text-sm"
            >
              Back to sign in
            </button>
          )}
        </div>
      </div>
    </div>
  );
}